import { useCustomerLanguages } from '../lib/i18n/useCustomerLanguages'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Alert, Button, Card, Modal, SearchField, Tabs } from '@heroui/react'
import { Chrome } from '../ui/Chrome'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { api } from '../lib/api'
import { backInMinutes, isHeld, positionOf } from '../lib/branchState'
import { requestBannerCount } from '../lib/pendingRequests'
import { bookedAhead, bookedMark } from '../lib/bookings'
import { callingWaitSeconds } from '../lib/callingWait'
import { dayName, useNow } from '../lib/clock'
import { explain } from '../lib/staffErrors'
import { formatMoment, nameIn, type StaffKey } from '../lib/staffText'
import { useBranchLive } from '../lib/useBranch'
import { issuedOn, placeFor } from '../lib/findResult'
import { currentServiceId } from '../lib/move'
import { missedAt } from '../lib/missed'
import { MAX_PLANNED_SERVICES, queueForService, serviceLabel } from '../lib/plan'
import { picturesForAll } from '../lib/pictures'
import { picturesOf, slipDocuments, slipFor, slipLogo } from '../lib/slips'
import { printTicketAndSay } from '../lib/printer'
import { optionOn } from '../lib/orgOptions'
import { SUPERVISE } from '../lib/putBack'
import { receptionSlip, slipLanguage } from '../lib/receptionSlip'
import { receptionKpis } from '../lib/receptionKpis'
import {
  estimateLabel,
  leaveWatcher,
  maskPhone,
  mergeDay,
  nameMatches,
  newestFirst,
  withSavedFields,
  overTarget,
  queueLetter,
  rowCustomer,
  slipPrintLine,
  ticketMatches,
  type SlipPrint,
} from '../lib/receptionDesk'
import { ordinalKey } from '../lib/receptionStatus'
import { draftPreview, slipHasCode, visitorFields } from '../lib/receptionPreview'
import { mayNotBeServed, type Estimate } from '../lib/estimate'
import { useEstimates } from '../lib/useEstimates'
import { fieldsToSend, offeredFields, readsDeclarations, valueProblem } from '../lib/ticketFields'
import type { LayoutDocument } from '../layout/types'
import { useLayout } from '../layout/useLayout'
import { QueueAlertBanner } from '../ui/QueueAlertBanner'
import { useColleagues } from '../ui/TransferredChip'
import { RequestBanner } from '../ui/RequestBanner'
import { EmptyState } from '../ui/parts'
import { mins } from '../ui/hallParts'
import { showToast } from '../ui/toast'
import {
  NOTE_LIMIT,
  type BookingView,
  type Identity,
  type Priority,
  type Service,
  type Ticket,
  type VisitField,
  type WorkProfile,
} from '../lib/types'
import { FieldRow } from './receptionFields'
import { BookingsToday, ReceptionBookings, useTodayBookings } from './receptionBookings'
import { ReceptionTicket } from './receptionTicket'
import { issuedPriority, offersPriority } from './receptionPriority'
import { CustomerFields, Receipt, type ReceiptProps, type SlipLanguage } from './receptionPreview'
import { issueBody, pickFull, pickOrder, togglePick } from './receptionPick'
import { ReceptionStatus, type StatusPanel, type Where } from './receptionStatus'
import { Confirm } from './receptionKit'
import '../styles/desk.css'
import '../styles/reception.css'

/**
 * Reception: pick services, print one ticket, find any ticket of the day.
 * The picks, priority and details belong to one customer and reset after each issue.
 */

type Draft = { fields: Record<string, string>; note: string }
type Kept = Draft & { k: number }
const EMPTY: Draft = { fields: {}, note: '' }

const hasAny = (d: Draft) => Object.values(d.fields).some((v) => v.trim() !== '') || d.note.trim() !== ''

type SlipState = {
  ticket: Ticket
  serviceId: string
  position?: number
  /** The server's estimate at issue, until the live one has the ticket. */
  estimate?: Estimate
  booked?: boolean
  /** kept: put back after a reload, so whether it printed is not known. */
  print: SlipPrint
  again?: string
  k: number
}

// The customer-details bar. Hidden for now: tickets issue with no details.
// True brings it back as it was.
const SHOW_CUSTOMER_BAR = false

/** Reception's two page tabs; the bookings one is addressed as ?tab=booking. */
type ReceptionPage = 'desk' | 'booking'
const PAGE_PARAM = 'tab'
const pageInAddress = (): ReceptionPage => (new URLSearchParams(window.location.search).get(PAGE_PARAM) === 'booking' ? 'booking' : 'desk')

const SOURCE_KEY: Record<Ticket['source'], StaffKey> = {
  kiosk: 'reception.from.kiosk',
  reception: 'reception.from.reception',
  counter: 'reception.from.counter',
  mobile: 'reception.from.mobile',
  appointment: 'reception.from.appointment',
}

export function Reception({
  identity,
  onNavigate,
  onSignOut,
}: {
  // The whole identity rather than the person: the values panel turns on a
  // permission, and the permissions only come with the identity.
  identity: Identity
  onNavigate: (to: string) => void
  onSignOut: () => void
}) {
  const staff = identity.staff
  const { lang, say } = useStaffText()
  const customerLanguages = useCustomerLanguages()
  const live = useBranchLive('reception', { lang })
  const now = useNow()
  const [toast, sayBack] = useToast()
  const [services, setServices] = useState<Service[]>([])
  const [profiles, setProfiles] = useState<WorkProfile[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [priority, setPriority] = useState<Priority>('standard')
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  // Null sends no language, so the slip prints as it always did (receptionSlip's fallback).
  const [language, setLanguage] = useState<SlipLanguage | null>(null)
  // Details parked for a customer who stepped aside. Kept in this page's memory
  // and nowhere else: they are personal values, and browser storage would keep
  // them on a shared PC after the day ends.
  const [kept, setKept] = useState<Kept[]>([])
  // Booked customers' own names by their ticket, today's: names only.
  const [booked, setBooked] = useState<Record<string, string>>({})
  // Values saved here, by ticket: newer than the live copy until the ticket next moves.
  const [savedFields, setSavedFields] = useState<Record<string, Ticket['fields']>>({})
  // The services picked for the next ticket, in pick order.
  const [picked, setPicked] = useState<string[]>([])
  // Print is waiting on "Issue with these details?".
  const [asking, setAsking] = useState(false)
  const [slip, setSlip] = useState<SlipState | null>(null)
  const [slipVoiding, setSlipVoiding] = useState(false)
  // This machine's printer as the last print here found it; unknown until one is tried.
  const [printer, setPrinter] = useState<'ready' | 'failed' | null>(null)
  const [day, setDay] = useState<Ticket[]>([])
  const [svcQuery, setSvcQuery] = useState('')
  const [opened, setOpenedTo] = useState<{ id: string; start?: 'move' | 'cancel' } | null>(null)
  const setOpened = (id: string | null, start?: 'move' | 'cancel') => setOpenedTo(id ? { id, start } : null)
  const [panel, setPanel] = useState<StatusPanel>('tickets')
  // Find a ticket: filters the status card's list as it is typed.
  const [query, setQuery] = useState('')
  const statusRef = useRef<HTMLDivElement>(null)
  const outRef = useRef<HTMLDivElement>(null)
  const detailsRef = useRef<HTMLDivElement>(null)

  // What this organisation says a ticket may carry. Null until the answer is in,
  // so the panel is drawn once with the real declarations rather than first with
  // none; and null for good where the person may not read them (lib/ticketFields:
  // the write route would take reception, the listing might not).
  //
  // A failure leaves it null rather than empty. "Nothing is declared" and "the
  // declarations could not be read" are different facts, and the second must not
  // be shown as the first. It is not announced either: nothing else here depends
  // on it, and a greeter would meet the message every morning.
  const [declared, setDeclared] = useState<VisitField[] | null>(null)
  const mayEditFields = readsDeclarations(identity.permissions)
  const offered = useMemo(() => (declared ? offeredFields(declared) : []), [declared])

  const branchId = live.branch?.id
  const tenantId = live.branch?.tenantId
  const zone = live.branch?.timezone
  const state = live.state
  const estimates = useEstimates(branchId, state)
  const businessDate = state?.businessDate

  // The branch's chosen ticket design, or the built-in one — the same slip a kiosk
  // prints — and the services that print something else.
  const { document: slipDesign, ticketByService: slipsByService } = useLayout(branchId, 'ticket')
  // The pictures those designs refer to, fetched once per design. Cancelled on a
  // design change, so a logo fetched for yesterday's design never lands on today's.
  const [slipPictures, setSlipPictures] = useState<Record<string, string>>({})
  useEffect(() => {
    let current = true
    void picturesForAll(slipDocuments(slipDesign, slipsByService), api.assetURL).then((got) => {
      if (current) setSlipPictures(got)
    })
    return () => {
      current = false
    }
  }, [slipDesign, slipsByService])

  const canSupervise = identity.permissions.includes(SUPERVISE)
  const bookOnline = optionOn(identity, 'onlineBooking')
  const priorityOffered = offersPriority(identity)
  // A day nobody closed. The server works this out, so every client agrees.
  const unclosed = state?.unclosedFrom ?? ''
  const requestCount = requestBannerCount(state?.requests, identity.permissions)

  // The Online booking tab is there while the option is on or today has bookings.
  const bookings = useTodayBookings(branchId)
  const bookingTab = bookOnline || bookings.list.length > 0
  const [chosenPage, setChosenPage] = useState<ReceptionPage>(pageInAddress)
  useEffect(() => {
    const onPop = () => setChosenPage(pageInAddress())
    addEventListener('popstate', onPop)
    return () => removeEventListener('popstate', onPop)
  }, [])
  const page: ReceptionPage = bookingTab ? chosenPage : 'desk'
  const choosePage = (next: ReceptionPage) => {
    if (next === chosenPage) return
    const params = new URLSearchParams(window.location.search)
    if (next === 'booking') params.set(PAGE_PARAM, next)
    else params.delete(PAGE_PARAM)
    const query = params.toString()
    window.history.pushState({}, '', window.location.pathname + (query ? `?${query}` : ''))
    setChosenPage(next)
    setAsking(false)
  }

  useEffect(() => {
    if (!branchId) return
    api
      .services(branchId)
      .then((r) => setServices(r.services))
      .catch((err) => setError(explain(err, lang, 'common.unreachable')))
    // The profiles say which desks serve which line (the status panel's letters).
    setProfiles(null)
    api
      .profiles(branchId)
      .then((r) => setProfiles(r.profiles))
      .catch(() => undefined)
    setSlip(null)
    setPicked([])
    // lang deliberately not a dependency: a language switch must not refetch.
  }, [branchId])

  // The declarations belong to the organisation the chosen branch is in, which is
  // the branch's tenant and not necessarily the reader's.
  useEffect(() => {
    if (!mayEditFields || !branchId) return
    let current = true
    api
      .declaredFields()
      .then((r) => current && setDeclared(r.fields))
      .catch(() => undefined)
    return () => {
      current = false
    }
  }, [mayEditFields, branchId, tenantId])

  // The day's tickets. The live channel carries open tickets only, so the closed
  // and cancelled ones come from here: read on arrival, every minute, and soon
  // after an open ticket leaves the live list — that is a ticket that has just
  // finished or been cancelled, whose ending this list has not seen yet.
  const loadDay = useCallback(async () => {
    if (!branchId || !businessDate) return
    try {
      const r = await api.ticketsForDay(branchId, businessDate)
      setDay(r.tickets ?? [])
    } catch {
      // The list keeps what it had; the open tickets still arrive live.
    }
  }, [branchId, businessDate])
  useEffect(() => {
    setDay([])
    void loadDay()
    const timer = window.setInterval(() => void loadDay(), 60_000)
    return () => window.clearInterval(timer)
  }, [loadDay])
  // A reload loses the slip; the day's newest reception ticket stands in for it.
  const slipRestored = useRef<string | null>(null)
  useEffect(() => {
    if (slip || !branchId || slipRestored.current === branchId) return
    const last = day.filter((t) => t.source === 'reception').sort(newestFirst)[0]
    if (!last) return
    slipRestored.current = branchId
    setSlip({ ticket: last, serviceId: currentServiceId(last), print: 'kept', k: Date.now() })
  }, [day, slip, branchId])
  const loadDayNow = useRef(loadDay)
  useEffect(() => {
    loadDayNow.current = loadDay
  }, [loadDay])
  const [leaving] = useState(() => leaveWatcher(() => void loadDayNow.current()))
  useEffect(() => () => leaving.stop(), [leaving])
  useEffect(() => leaving.seen((state?.tickets ?? []).map((t) => t.id)), [state?.tickets, leaving])

  /** A ticket as the server just answered it, into the day at once. */
  const upsert = useCallback((ticket: Ticket) => {
    setDay((current) => [ticket, ...current.filter((t) => t.id !== ticket.id)])
  }, [])

  const tickets = useMemo(() => withSavedFields(mergeDay(day, state?.tickets ?? []), savedFields), [day, state?.tickets, savedFields])
  // Read again when another booked customer arrives.
  const arrivals = tickets.filter((t) => t.appointmentId).length
  useEffect(() => {
    if (!branchId || arrivals === 0) return
    let stillHere = true
    api
      .bookedNames(branchId)
      .then((r) => stillHere && setBooked(r.names))
      .catch(() => undefined)
    return () => {
      stillHere = false
    }
  }, [branchId, arrivals])
  const byId = useMemo(() => new Map(tickets.map((t) => [t.id, t])), [tickets])
  // A booking arrived elsewhere (a kiosk, another desk) shows up as a new arrival here.
  const seenArrivals = useRef(arrivals)
  const reloadBookings = bookings.load
  useEffect(() => {
    if (arrivals === seenArrivals.current) return
    seenArrivals.current = arrivals
    void reloadBookings()
  }, [arrivals, reloadBookings])

  // Paper, after the number is on the screen and never before it. A failure is
  // said on the slip: unlike a kiosk, somebody here can say the number out loud.
  async function printSlip(ticket: Ticket, serviceId: string, booked = false) {
    if (!branchId) return
    let position: number | undefined
    let estimate: Estimate | undefined
    // A booked arrival gets the wait (the server plays booked times forward) but no
    // place: before its time, "3rd in line" is not a place this customer holds.
    try {
      const place = placeFor(ticket.id, await api.findByNumber(branchId, ticket.displayNumber))
      if (!booked) position = place.position
      estimate = place.estimate
    } catch {
      // Printed without a place rather than not at all: see receptionSlip.
    }
    // A fresh link for this slip. Printing again replaces the old one, which is
    // what a customer who lost their slip needs: the lost code stops working.
    let followUrl: string | undefined
    let validUntil: string | undefined
    try {
      const link = await api.followLink(ticket.id)
      followUrl = link.followUrl
      validUntil = link.validUntil
    } catch {
      followUrl = undefined
    }
    const design = slipFor(serviceId, slipDesign, slipsByService)
    const ok = await printTicketAndSay(
      receptionSlip({
        languages: customerLanguages,
        ticket,
        position,
        estimate,
        service: services.find((s) => s.id === serviceId),
        branch: live.branch ?? undefined,
        design,
        pictures: picturesOf(design, slipPictures) ?? {},
        followUrl,
        validUntil,
      }),
    )
    setSlip((s) => (s && s.ticket.id === ticket.id ? { ...s, print: ok ? 'printed' : 'failed' } : s))
    setPrinter(ok ? 'ready' : 'failed')
  }

  /** The same ticket out of the slot again, drawn and (when it can) printed. */
  function printAgain(ticket: Ticket) {
    const serviceId = currentServiceId(ticket)
    // Before its booked time an arrival holds no place in the line (see printSlip).
    const booked = bookedAhead(ticket, Date.now())
    setSlip({ ticket, serviceId, booked, print: 'printing', again: formatMoment(new Date(), lang, { time: true, zone }), k: Date.now() })
    setSlipVoiding(false)
    void printSlip(ticket, serviceId, booked)
  }

  const fieldProblems = useMemo(() => {
    const out: Record<string, string> = {}
    for (const f of offered) {
      const p = valueProblem(f, (draft.fields[f.key] ?? '').trim(), lang)
      if (p) out[f.key] = p
    }
    return out
  }, [offered, draft.fields, lang])
  const detailsBad = Object.keys(fieldProblems).length > 0
  const extras = hasAny(draft)

  async function issue(withDetails: boolean) {
    setAsking(false)
    if (!branchId || busy || picked.length === 0) return
    if (withDetails && detailsBad) {
      if (SHOW_CUSTOMER_BAR) setDetailsOpen(true)
      return
    }
    const body = { ...issueBody(picked, issuedPriority(priorityOffered, priority)), ...(language ? { language } : {}) }
    const serviceId = body.serviceId
    const dropped = slipVoiding && slip ? slip.ticket : null
    setBusy(true)
    setError('')
    try {
      const fields = withDetails ? fieldsToSend(offered, draft.fields) : {}
      const note = withDetails ? draft.note.trim() : ''
      const result = await api.issueTicket(branchId, {
        ...body,
        note: note || undefined,
        ...(Object.keys(fields).length > 0 ? { fields } : {}),
      })
      upsert(result.ticket)
      setSlip({
        ticket: result.ticket,
        serviceId,
        position: result.position,
        estimate: result.estimate ?? undefined,
        print: 'printing',
        k: Date.now(),
      })
      setSlipVoiding(false)
      // Not awaited: the number is already on screen, and the next press must not
      // wait on a printer.
      void printSlip(result.ticket, serviceId)
      // All of these belong to the customer who has just walked away.
      setPicked([])
      setPriority('standard')
      setLanguage(null)
      if (withDetails) {
        setDraft(EMPTY)
        setDetailsOpen(false)
      }
      sayBack(say('reception.issued', { number: result.ticket.displayNumber }))
      // A cancel question left open on the previous slip is dropped, and said so.
      if (dropped) sayBack(say('reception.notCancelled', { number: dropped.displayNumber }), true)
      // On a phone the slip is below the services; bring it into view.
      if (matchMedia('(max-width: 900px)').matches) {
        requestAnimationFrame(() => outRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
      }
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  // The preview's form sits beside Print, so its details go with the ticket. The
  // hidden customer bar still asks first.
  const printPicked = () => (extras && SHOW_CUSTOMER_BAR ? setAsking(true) : void issue(extras))
  // Everything about the customer being prepared, picks included.
  const clearCustomer = () => {
    setPicked([])
    setDraft(EMPTY)
    setPriority('standard')
    setLanguage(null)
  }
  const visitorBox = visitorFields(offered)
  const setField = (key: string, value: string) => setDraft((d) => ({ ...d, fields: { ...d.fields, [key]: value } }))
  const toggle = (serviceId: string) => setPicked((p) => togglePick(p, serviceId))
  const full = pickFull(picked)

  /** "Leyla", or something else recognisable about what was typed. */
  const whoOf = (d: Draft): string => {
    for (const f of offered) {
      const v = (d.fields[f.key] ?? '').trim()
      if (v) return f.type === 'phone' ? maskPhone(v) : v
    }
    return d.note.trim() ? `“${d.note.trim().slice(0, 24)}”` : ''
  }

  const keepForLater = () => {
    if (!extras) return
    setKept((k) => [...k, { ...draft, k: Date.now() }])
    sayBack(say('reception.kept'))
    setDraft(EMPTY)
    setDetailsOpen(false)
    setAsking(false)
  }
  const takeKept = (k: Kept) => {
    // Whatever is in the form now is kept, not thrown away.
    setKept((all) => [...all.filter((x) => x.k !== k.k), ...(extras ? [{ ...draft, k: Date.now() }] : [])])
    setDraft({ fields: k.fields, note: k.note })
    setDetailsOpen(true)
    setAsking(false)
  }

  async function cancelSlip(ticket: Ticket) {
    setError('')
    try {
      const { ticket: back } = await api.ticketAction(ticket.id, 'void', { reason: 'withdrawn at reception' })
      upsert(back)
      setSlip((s) => (s && s.ticket.id === back.id ? { ...s, ticket: back } : s))
      sayBack(say('reception.cancelled', { number: ticket.displayNumber }))
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setSlipVoiding(false)
    }
  }

  // The service tiles as filtered by the service search.
  const offeredServices = useMemo(() => {
    if (!svcQuery.trim()) return services
    return services.filter((s) => nameMatches(nameIn(s.name, lang, s.code), svcQuery) || nameMatches(s.code, svcQuery))
  }, [services, svcQuery, lang])

  // Find: a single match opens; otherwise the list it filtered comes into view.
  const findTicket = () => {
    setPanel('tickets')
    const found = query.trim() ? tickets.filter((t) => ticketMatches(t, query)) : []
    if (found.length === 1) setOpened(found[0]!.id)
    else requestAnimationFrame(() => statusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  const deskName = (id: string | undefined) => (id ? state?.counters.find((c) => c.counterId === id)?.name ?? '' : '')
  const colleagues = useColleagues(branchId, identity.permissions)
  const time = (iso: string | undefined) => (iso ? formatMoment(iso, lang, { time: true, zone }) : '')
  /** The wait as Reception says it, with "may not be served today" after it. */
  const waitWords = (e: Estimate | null | undefined) => {
    const words = estimateLabel(e, say)
    return words && mayNotBeServed(e) ? `${words} · ${say('estimate.late')}` : words
  }

  const queueName = (id: string | undefined) => {
    const q = state?.queues.find((x) => x.queueId === id)
    return q ? nameIn(q.name, lang, q.code) : ''
  }
  const firstName = (name: string | undefined) => (name ?? '').trim().split(/\s+/)[0] ?? ''

  const where = (t: Ticket): Where => {
    switch (t.state) {
      case 'waiting': {
        const waited = callingWaitSeconds(t, now)
        const waitedText = waited < 60 ? say('reception.where.justJoined') : say('reception.where.waited', { time: mins(waited, say) })
        let place = say('common.state.waiting')
        let about = ''
        if (isHeld(t, now)) place = say('reception.where.held', { m: backInMinutes(t, now) })
        else if (bookedAhead(t, now)) place = bookedMark(t, lang, zone)!
        else {
          // The server's place and wait; the live line's count only until it has the ticket.
          const told = estimates.tickets.get(t.id)
          const pos = told?.position ?? (state ? positionOf(state, t, now) : 0)
          if (pos === 1) place = say('reception.where.next')
          else if (pos > 1) {
            place = say('reception.where.nthInLine', { nth: say(ordinalKey(pos), { n: pos }) })
            about = waitWords(told)
          }
        }
        if (overTarget(t, now)) {
          return { main: say('reception.where.over', { time: mins(waited, say) }), sub: [place, queueName(t.queueId)].filter(Boolean).join(' · '), tone: 'bad', over: true }
        }
        return { main: place, sub: [queueName(t.queueId), waitedText, about].filter(Boolean).join(' · ') }
      }
      case 'called': {
        const counter = state?.counters.find((c) => c.counterId === t.calledToCounterId)
        return { main: say('reception.where.called', { desk: counter?.name || '—' }), sub: firstName(counter?.staffName) || undefined, tone: 'call' }
      }
      case 'serving':
      case 'wrapup': {
        const counter = state?.counters.find((c) => c.counterId === t.calledToCounterId)
        const since = t.transactions[t.currentTransaction]?.startedAt ?? t.lastCalledAt
        const there = since ? mins(Math.max(0, (now - Date.parse(since)) / 1000), say) : ''
        return {
          main: say('reception.where.atDesk', { desk: counter?.name ?? '—' }),
          sub: [firstName(counter?.staffName), there].filter(Boolean).join(' · ') || undefined,
          tone: 'ok',
        }
      }
      case 'parked':
        return { main: say('reception.where.parked'), sub: deskName(t.poolCounterId) || undefined }
      case 'no_show': {
        const at = missedAt(t)
        return { main: at ? say('reception.where.missed', { desk: deskName(at) || '—' }) : say('common.state.no_show'), tone: 'warn' }
      }
      case 'void':
        return { main: say('reception.where.cancelled', { time: time(t.closedAt) }), tone: 'off' }
      default:
        return { main: say('reception.where.finished', { time: time(t.closedAt) }), tone: 'off' }
    }
  }

  const customerOf = (t: Ticket) => rowCustomer(t, offered, booked[t.id])

  const slipTicket = slip ? byId.get(slip.ticket.id) ?? slip.ticket : undefined
  const open = opened ? byId.get(opened.id) : undefined
  const serviceName = (id: string) => serviceLabel(services, id, lang)
  const pickedNames = picked.map(serviceName)
  // A search can hide a picked tile; the print bar then names every pick in order.
  const picksHidden = picked.some((id) => !offeredServices.some((s) => s.id === id))

  const factsOf = (t: Ticket) => {
    const parts = [serviceName(currentServiceId(t)), time(t.issuedAt), say(SOURCE_KEY[t.source])]
    const other = issuedOn(t, businessDate, lang)
    if (other) parts.splice(2, 0, other)
    return parts.filter(Boolean).join(', ')
  }

  const kpis = useMemo(() => receptionKpis(state, bookings.list, now), [state, bookings.list, now])
  const kpiCards: Array<{ key: string; label: StaffKey; sub?: StaffKey; value: string | number; warn?: boolean }> = [
    { key: 'waiting', label: 'reception.kpi.waiting', value: kpis.waiting },
    { key: 'longest', label: 'reception.kpi.longest', value: mins(kpis.longestSec, say) },
    { key: 'desks', label: 'reception.kpi.desks', value: kpis.desksOpen },
    // Bookings show only where the Online booking tab does.
    ...(bookingTab ? [{ key: 'bookings', label: 'reception.kpi.bookings' as const, sub: 'reception.kpi.bookingsSub' as const, value: kpis.bookingsDue }] : []),
    { key: 'over', label: 'reception.kpi.over', value: kpis.overTarget, warn: kpis.overTarget > 0 },
  ]

  // The preview: the ticket being prepared, else the last one printed, else blank paper.
  const preview: ReceiptProps = (() => {
    const branch = live.branch?.name ?? ''
    const clock = (at: string | Date) => ({ date: formatMoment(at, lang, { date: true, zone }), time: formatMoment(at, lang, { time: true, zone }) })
    const place = (pos: number) => (pos === 1 ? say('reception.youAreNext') : say(ordinalKey(pos), { n: pos }))
    const logoOf = (design: LayoutDocument | null) => {
      const id = slipLogo(design)
      return id ? api.assetURL(id) : undefined
    }
    if (picked.length > 0) {
      const first = picked[0]!
      const d = draftPreview(first, issuedPriority(priorityOffered, priority), state?.queues ?? [], estimates.queues)
      return {
        kind: 'draft',
        label: say('reception.preview'),
        branch,
        number: (
          <>
            {d.letter && `${d.letter}-`}
            <span className="rx-slip__unknown">—</span>
          </>
        ),
        service: serviceName(first),
        priority: issuedPriority(priorityOffered, priority),
        then: picked.slice(1).map(serviceName),
        ...clock(new Date(now)),
        position: d.estimate && d.estimate.position > 0 ? place(d.estimate.position) : undefined,
        wait: waitWords(d.estimate) || undefined,
        code: slipHasCode(slipFor(first, slipDesign, slipsByService)),
        logo: logoOf(slipFor(first, slipDesign, slipsByService)),
      }
    }
    if (slip && slipTicket) {
      const t = slipTicket
      const waiting = t.state === 'waiting'
      // Where they stand now, from the server; its answer at issue until the estimates have them.
      const told = waiting ? estimates.tickets.get(t.id) : undefined
      const pos = waiting ? (told?.position ?? slip.position ?? 0) : 0
      const booked = slip.booked && waiting ? bookedMark(t, lang, zone) : null
      const line = slipPrintLine(slip.print, !!slip.again, waiting)
      const meta =
        line === 'printing'
          ? say('reception.printing')
          : line === 'failed'
            ? say('reception.didNotPrint', { number: t.displayNumber })
            : line === 'again'
              ? say('reception.printedAgain', { time: slip.again ?? '' })
              : line === 'printed'
                ? say('reception.printed')
                : ''
      return {
        kind: 'ticket',
        label: say('reception.ticketTitle', { number: t.displayNumber }),
        state: t.state,
        branch,
        number: t.displayNumber,
        service: serviceName(slip.serviceId),
        priority: t.priority,
        then: (t.plannedServices ?? []).map(serviceName),
        ...clock(t.issuedAt),
        status: !waiting
          ? t.state === 'void'
            ? say('reception.where.cancelled', { time: formatMoment(t.closedAt ?? new Date(), lang, { time: true, zone }) })
            : say(`common.state.${t.state}` as StaffKey)
          : (booked ?? undefined),
        position: pos > 0 ? place(pos) : undefined,
        wait: pos > 1 ? waitWords(told ?? slip.estimate) || undefined : undefined,
        code: slipHasCode(slipFor(slip.serviceId, slipDesign, slipsByService)),
        logo: logoOf(slipFor(slip.serviceId, slipDesign, slipsByService)),
        meta: meta ? { text: meta, failed: slip.print === 'failed' } : undefined,
      }
    }
    return { kind: 'empty', label: say('reception.preview'), branch, ...clock(new Date(now)), code: slipHasCode(slipDesign), logo: logoOf(slipDesign) }
  })()

  /** A booked customer arrived here: their ticket, drawn and printed like any other. */
  const arrived = (ticket: Ticket, booking: BookingView) => {
    upsert(ticket)
    setSlip({ ticket, serviceId: booking.serviceId, booked: true, print: 'printing', k: Date.now() })
    setSlipVoiding(false)
    void printSlip(ticket, booking.serviceId, true)
    sayBack(say('reception.issued', { number: ticket.displayNumber }))
  }
  const notices = !!(error || live.error || unclosed || state?.alerts?.length || requestCount > 0)
  const letterOfService = (serviceId: string) => {
    const queue = state ? queueForService(state.queues, serviceId) : undefined
    return queue ? queueLetter(queue) : ''
  }

  if (live.noBranch)
    return (
      <Chrome title={say('entry.app.reception')} staff={staff} onNavigate={onNavigate} onSignOut={onSignOut}>
        <main className="page rx">
          <Card>
            <EmptyState icon={<Icon name="organisations" />} title={say('common.noBranchesYet')} />
          </Card>
        </main>
      </Chrome>
    )

  return (
    <Chrome
      title={say('entry.app.reception')}
      staff={staff}
      branch={live.branch}
      branches={live.branches}
      status={live.status}
      onChooseBranch={live.chooseBranch}
      onNavigate={onNavigate}
      onSignOut={onSignOut}
    >
      <main className="page page--work reception rx">
        {notices && (
          <div className="rx-notices">
            {(error || live.error) && (
              <Alert status="danger">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>{error || live.error}</Alert.Title>
                </Alert.Content>
              </Alert>
            )}
            {unclosed && (
              <Alert status="warning">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>{say('reception.unclosed', { day: dayName(unclosed, lang) })}</Alert.Title>
                </Alert.Content>
              </Alert>
            )}
            <QueueAlertBanner state={state} zone={zone} />
            <RequestBanner state={state} permissions={identity.permissions} viewerStaffId={staff.id} branchId={branchId} onNavigate={onNavigate} />
          </div>
        )}

        <header className="rx-head">
          <div className="rx-head__text">
            <h1 className="page__title">{say('entry.app.reception')}</h1>
            {/* Drawn before the branch is known, so the page does not move down when it arrives. */}
            <p className="rx-head__sub">{live.branch ? say('reception.inHall', { branch: live.branch.name, n: kpis.waiting }) : '\u00a0'}</p>
          </div>
          {page === 'desk' && (
            <form
              className="rx-find"
              role="search"
              onSubmit={(e) => {
                e.preventDefault()
                findTicket()
              }}
            >
              <SearchField aria-label={say('reception.findTicket')} value={query} onChange={setQuery} className="rx-find__field">
                <SearchField.Group>
                  <SearchField.SearchIcon />
                  <SearchField.Input placeholder={say('reception.findTicket')} autoComplete="off" />
                  <SearchField.ClearButton />
                </SearchField.Group>
              </SearchField>
              <Button type="submit" variant="tertiary" size="sm">
                {say('reception.find')}
              </Button>
            </form>
          )}
        </header>

        <ul className="rx-kpis">
          {kpiCards.map((c) => (
            <li key={c.key} className="rx-kpi">
              <span>{say(c.label)}</span>
              <b data-warn={c.warn || undefined}>{c.value}</b>
              {c.sub && (
                <span className="rx-kpi__n" data-warn={c.warn || undefined}>
                  {say(c.sub)}
                </span>
              )}
            </li>
          ))}
        </ul>

        {bookingTab && (
          <Tabs selectedKey={page} onSelectionChange={(k) => choosePage(String(k) as ReceptionPage)} className="rx-pages">
            <Tabs.ListContainer>
              <Tabs.List aria-label={say('entry.app.reception')}>
                <Tabs.Tab id="desk">
                  {say('reception.page.desk')}
                  <Tabs.Indicator />
                </Tabs.Tab>
                <Tabs.Tab id="booking">
                  {say('reception.page.booking')}
                  <Tabs.Indicator />
                </Tabs.Tab>
              </Tabs.List>
            </Tabs.ListContainer>
          </Tabs>
        )}

        {page === 'booking' && branchId && (
          <ReceptionBookings
            branchId={branchId}
            bookings={bookings}
            zone={zone}
            modules={identity.modules}
            bookOnline={bookOnline}
            declared={declared ?? []}
            services={services}
            onArrived={arrived}
          />
        )}

        {page === 'desk' && SHOW_CUSTOMER_BAR && (
          <div className="toolstrip reception-bar">
            <div className="toolstrip__item reception-bar__who">
              <span className="label-caps">{say('reception.col.customer')}</span>
              <div className="reception-bar__row">
                <Button
                  variant="outline"
                  size="sm"
                  aria-expanded={detailsOpen}
                  onPress={() => {
                    setDetailsOpen(!detailsOpen)
                    if (!detailsOpen) requestAnimationFrame(() => (detailsRef.current?.querySelector<HTMLElement>('input') ?? detailsRef.current?.querySelector<HTMLElement>('button'))?.focus())
                  }}
                >
                  <Icon name={detailsOpen ? 'up' : extras ? 'user' : 'plus'} size={15} />
                  <span>{extras ? say('reception.detailsOf', { who: whoOf(draft) }) : say('reception.addDetails')}</span>
                </Button>
                {kept.map((k) => (
                  <span key={k.k} className="reception-kept">
                    <button type="button" className="reception-kept__use" onClick={() => takeKept(k)}>
                      <span>{whoOf(k)}</span>
                      <strong>{say('reception.use')}</strong>
                    </button>
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      aria-label={say('reception.deleteKept', { who: whoOf(k) })}
                      onPress={() => setKept((all) => all.filter((x) => x.k !== k.k))}
                    >
                      <Icon name="close" size={13} />
                    </Button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {page === 'desk' && (
          <div className="rx-grid">
            <div className="rx-col">
              <Card className="rx-issue" aria-labelledby="reception-issue-title">
                <Card.Header className="rx-card-h">
                  <Card.Title id="reception-issue-title" render={(props) => <h2 {...props} />}>
                    {say('reception.issue')}
                  </Card.Title>
                  <SearchField aria-label={say('reception.findService')} variant="secondary" value={svcQuery} onChange={setSvcQuery} className="rx-svcfind">
                    <SearchField.Group>
                      <SearchField.SearchIcon />
                      <SearchField.Input placeholder={say('reception.findService')} autoComplete="off" />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>
                </Card.Header>


                {detailsOpen && (
                  <div ref={detailsRef} className="reception-details">
                    {offered.map((field) => (
                      <FieldRow
                        key={field.key}
                        field={field}
                        value={draft.fields[field.key] ?? ''}
                        problem={fieldProblems[field.key] ?? ''}
                        onChange={(value) => setField(field.key, value)}
                      />
                    ))}
                    <label className="field reception-details__note">
                      <span>{say('reception.noteForDesk')}</span>
                      <input
                        value={draft.note}
                        maxLength={NOTE_LIMIT}
                        autoComplete="off"
                        onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                      />
                    </label>
                    <div className="reception-details__foot">
                      <Button
                        variant="ghost"
                        size="sm"
                        isDisabled={!extras}
                        onPress={() => {
                          setDraft(EMPTY)
                          setAsking(false)
                        }}
                      >
                        {say('common.clear')}
                      </Button>
                      <Button variant="secondary" size="sm" isDisabled={!extras} onPress={keepForLater}>
                        {say('reception.keepForLater')}
                      </Button>
                    </div>
                  </div>
                )}

                {services.length === 0 ? (
                  <p className="rx-empty">{say('reception.noServices')}</p>
                ) : offeredServices.length === 0 ? (
                  <p className="rx-empty">{say('reception.noMatch', { q: svcQuery.trim() })}</p>
                ) : (
                  <ul className="rx-svcs" aria-label={say('reception.col.service')}>
                    {offeredServices.map((s) => {
                      const queue = state ? queueForService(state.queues, s.id) : undefined
                      const n = queue?.waiting ?? 0
                      const told = queue ? estimates.queues.get(queue.queueId) : undefined
                      const wait = waitWords(told)
                      const order = pickOrder(picked, s.id)
                      const shut = full && order === 0
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            className="rx-svc"
                            onClick={() => !busy && !shut && toggle(s.id)}
                            aria-disabled={busy || shut || undefined}
                            aria-pressed={order > 0}
                            data-full={shut || undefined}
                            title={shut ? say('reception.atMost', { n: MAX_PLANNED_SERVICES }) : undefined}
                          >
                            {order > 0 && <span className="rx-svc__order">{order}</span>}
                            <span className="rx-svc__qt">{queue && queueLetter(queue)}</span>
                            <b className="rx-svc__name">{nameIn(s.name, lang, s.code)}</b>
                            <span className="rx-svc__load" data-over={(queue?.breaching ?? 0) > 0 || undefined} title={told?.noDesk ? say('estimate.noDesk') : undefined}>
                              {n === 0
                                ? told?.noDesk
                                  ? say('estimate.nobodyWaiting', { wait })
                                  : say('reception.nobodyWaiting')
                                : wait
                                  ? say('estimate.nWaiting', { n, wait })
                                  : say('reception.nWaiting', { n })}
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}

                {(picksHidden || full) && (
                  <Card.Footer className="rx-issue__foot">
                    {picksHidden && (
                      <ol className="rx-picks" title={pickedNames.join(', ')}>
                        {pickedNames.map((name, i) => (
                          <li key={picked[i]}>{name}</li>
                        ))}
                      </ol>
                    )}
                    {full && <small className="rx-muted">{say('reception.atMost', { n: MAX_PLANNED_SERVICES })}</small>}
                  </Card.Footer>
                )}
              </Card>

              <ReceptionStatus
                ref={statusRef}
                panel={panel}
                onPanel={setPanel}
                tickets={tickets}
                state={state}
                profiles={profiles}
                now={now}
                query={query}
                where={where}
                customerOf={customerOf}
                serviceName={serviceName}
                time={time}
                businessDate={businessDate}
                canSupervise={canSupervise}
                colleagues={colleagues}
                onOpen={setOpened}
              />
            </div>

            <div className="rx-col">
              <Card ref={outRef} className="rx-out" aria-labelledby="reception-out-title">
                <Card.Header className="rx-card-h">
                  <Card.Title id="reception-out-title" render={(props) => <h2 {...props} />}>
                    {preview.kind === 'ticket' ? say('reception.lastSlip') : say('reception.preview')}
                  </Card.Title>
                  {printer && (
                    <span className="rx-printer" data-printer={printer}>
                      {say(printer === 'ready' ? 'reception.printerReady' : 'reception.printerFailed')}
                    </span>
                  )}
                </Card.Header>
                <div role="status">
                  <Receipt key={preview.kind === 'ticket' ? slip?.k : preview.kind} {...preview} />
                </div>
                {preview.kind === 'ticket' && slipTicket?.state === 'waiting' && (
                  <Card.Footer className="rx-slipdo">
                    {slipVoiding ? (
                      <Confirm
                        question={say('reception.cancelAsk', { number: slipTicket.displayNumber })}
                        yes={say('reception.yesCancel', { number: slipTicket.displayNumber })}
                        no={say('reception.keepIt')}
                        onYes={() => void cancelSlip(slipTicket)}
                        onNo={() => setSlipVoiding(false)}
                      />
                    ) : (
                      <>
                        <Button variant="secondary" size="sm" onPress={() => printAgain(slipTicket)}>
                          {say('reception.printAgain')}
                        </Button>
                        <Button variant="ghost" size="sm" className="rx-danger" onPress={() => setSlipVoiding(true)}>
                          {say('reception.cancelTicket')}
                        </Button>
                      </>
                    )}
                  </Card.Footer>
                )}
              </Card>

              {/* After the services: the slip's options and Print, under the slip they change. */}
              <Card className="rx-opts" aria-label={say('reception.printTicket')}>
                <CustomerFields
                  name={mayEditFields ? visitorBox.text : undefined}
                  phone={mayEditFields ? visitorBox.phone : undefined}
                  values={draft.fields}
                  problems={fieldProblems}
                  onValue={setField}
                  priorityOffered={priorityOffered}
                  priority={priority}
                  onPriority={setPriority}
                  language={slipLanguage(language ?? undefined, customerLanguages)}
                  languages={customerLanguages}
                  onLanguage={setLanguage}
                  note={draft.note}
                  onNote={(note) => setDraft((d) => ({ ...d, note }))}
                />
                <Card.Footer className="rx-opts__foot">
                  {picked.length > 0 && (
                    <Button variant="tertiary" size="sm" isDisabled={busy} onPress={clearCustomer}>
                      {say('common.clear')}
                    </Button>
                  )}
                  <Button variant="primary" size="sm" isDisabled={busy || detailsBad || picked.length === 0} onPress={printPicked}>
                    {picked.length > 0 ? say('reception.printTicket') : say('reception.selectService')}
                  </Button>
                </Card.Footer>
              </Card>

              {bookingTab && branchId && (
                <BookingsToday
                  branchId={branchId}
                  bookings={bookings}
                  declared={declared ?? []}
                  services={services}
                  letterOf={letterOfService}
                  zone={zone}
                  onArrived={arrived}
                />
              )}
            </div>
          </div>
        )}
      </main>

      <Modal.Backdrop isOpen={asking} onOpenChange={(open) => !open && setAsking(false)} className="rx-modal">
        <Modal.Container size="sm">
          <Modal.Dialog aria-describedby="reception-ask-who">
            <Modal.Header>
              <Modal.Heading>{say('reception.askWith')}</Modal.Heading>
            </Modal.Header>
            <Modal.Body>
              <p id="reception-ask-who" className="rx-muted">
                {[
                  pickedNames.join(', '),
                  ...offered.map((f) => {
                    const v = (draft.fields[f.key] ?? '').trim()
                    return v && f.type === 'phone' ? maskPhone(v) : v
                  }),
                  draft.note.trim() && `“${draft.note.trim()}”`,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
              {detailsBad && <p className="reception-error">{say('reception.fixFirst')}</p>}
            </Modal.Body>
            <Modal.Footer className="rx-modal__foot">
              <Button variant="ghost" size="sm" onPress={() => setAsking(false)}>
                {say('common.back')}
              </Button>
              <span className="rx-grow" />
              <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => void issue(false)}>
                {say('reception.noPlain')}
              </Button>
              <Button variant="primary" size="sm" autoFocus={!detailsBad} isDisabled={detailsBad || busy} onPress={() => void issue(true)}>
                {say('reception.yesWith')}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>

      {open && (
        <ReceptionTicket
          key={open.id}
          ticket={open}
          services={services}
          desks={state?.counters ?? []}
          declared={declared}
          where={where(open)}
          tone={where(open).tone}
          start={opened?.start}
          facts={factsOf(open)}
          customer={customerOf(open)}
          canPrint={open.state === 'waiting'}
          canSupervise={canSupervise}
          onClose={() => setOpened(null)}
          onPrint={() => {
            setOpened(null)
            printAgain(open)
          }}
          onDone={(ticket, said, close, notice) => {
            upsert(ticket)
            setSavedFields((s) => ({ ...s, [ticket.id]: ticket.fields }))
            setSlip((s) => (s && s.ticket.id === ticket.id ? { ...s, ticket } : s))
            if (close) setOpened(null)
            sayBack(said, notice)
          }}
        />
      )}
      {toast}
    </Chrome>
  )
}

/** A confirmation, six words or fewer; a notice is a warning and stays longer. The node is null. */
function useToast(): [ReactNode, (text: string, notice?: boolean) => void] {
  const show = useCallback((text: string, notice = false) => {
    if (notice) showToast(text, 'warning', 5000)
    else showToast(text)
  }, [])
  return [null, show]
}
